import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from '../utils/axios';
import { notifySuccess } from '../utils/notify';
import { useIsMobile } from '../utils/responsive';
import {
  activeSelectedLeafKeys,
  buildDisassemblyDeclaration,
  createMaterialNodes,
  expandedBranchesWithoutLeaves,
  normalizeSegment,
} from './returnsDisassemblyTree.mjs';

const apiPrefix = '/returns/receiving';

const toArray = (value) => (Array.isArray(value) ? value : []);
const PRICE_SOURCE_LABELS = {
  current_global: 'текущая глобальная',
  historical_global: 'историческая глобальная',
  buy_price: 'закупочная цена',
  profit_history: 'история прибыльности',
  store_fifo: 'FIFO склада возвратов',
  zero_fallback: 'цена 0 ₽',
};

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const formatQty = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
};

const normalizeItem = (item, idx = 0) => {
  const assortment = item?.assortment || item?.product || {};
  const assortmentId = firstValue(
    item?.assortment_id,
    item?.assortmentId,
    item?.ms_assortment_id,
    item?.assortment?.id,
    item?.product?.id
  );

  return {
    ...item,
    rowKey: String(firstValue(item?.id, item?.position_id, assortmentId, idx)),
    id: firstValue(item?.id, item?.position_id, assortmentId, idx),
    assortment_id: assortmentId,
    type: firstValue(item?.type, item?.assortment_type, assortment?.type, 'product'),
    name: firstValue(item?.name, item?.assortment_name, assortment?.name, 'Товар без названия'),
    quantity: asNumber(firstValue(item?.quantity, item?.qty, item?.count), 1),
    href: firstValue(item?.href, item?.meta?.href, assortment?.href, assortment?.meta?.href),
  };
};

const normalizeScanResponse = (data) => {
  const payload = data?.data || data || {};
  const orderSource = payload.order || payload.customerorder || payload.customerOrder || payload;
  const rawItems = toArray(
    payload.items || payload.positions || payload.products || orderSource.items || orderSource.positions || orderSource.products
  );
  const msOrderId = firstValue(
    orderSource.ms_order_id,
    orderSource.msOrderId,
    orderSource.customerorder_id,
    orderSource.customerOrderId,
    orderSource.ms_id
  );

  return {
    raw: payload,
    order: {
      ...orderSource,
      id: firstValue(orderSource.id, orderSource.order_id, msOrderId),
      customerorder_id: msOrderId,
      name: firstValue(orderSource.name, orderSource.order_name, orderSource.number, 'Заказ МойСклад'),
      href: firstValue(orderSource.href, orderSource.meta?.href, orderSource.ms_order_href, orderSource.msOrderHref, orderSource.ms_href, orderSource.msHref),
      created: firstValue(orderSource.created, orderSource.created_at, orderSource.moment),
      marketplace: firstValue(orderSource.marketplace, orderSource.mp, orderSource.agent_name, orderSource.agentName),
      stickerNum: firstValue(orderSource.stickerNum, orderSource.sticker_num, orderSource.sticker),
      barcodeMp: firstValue(orderSource.barcodeMp, orderSource.barcode_mp, orderSource.barcode),
    },
    items: rawItems.map(normalizeItem),
  };
};

const normalizeReturnDoc = (data) => {
  const payload = data?.data || data || {};
  const doc = payload.salesreturn || payload.return || payload.document || payload;
  return {
    raw: payload,
    id: firstValue(doc.id, doc.salesreturn_id, payload.salesreturn_id),
    name: firstValue(doc.name, doc.number, doc.salesreturn_name, payload.name, payload.salesreturn_name, 'Возврат покупателя'),
    href: firstValue(doc.href, doc.meta?.href, payload.href, payload.salesreturn_href),
    positions: toArray(payload.items || payload.positions || doc.items || doc.positions).map(normalizeItem),
  };
};

const normalizeMaterials = (data) => {
  const payload = data?.data || data || {};
  const techcard = payload.techcard || payload.tech_card || payload.techKarta || payload;
  const materials = toArray(payload.materials || techcard.materials || payload.rows);
  return {
    raw: payload,
    techcard: {
      id: firstValue(techcard.id, techcard.techcard_id, techcard.techkarta_id),
      name: firstValue(techcard.name, techcard.title, payload.techcard === null ? '' : 'Техкарта'),
      source: firstValue(payload.source, techcard.source),
      message: payload.message || '',
    },
    materials: materials.map((material, idx) => ({
      ...material,
      rowKey: String(firstValue(material.id, material.id_ms, material.assortment_id, material.assortmentId, idx)),
      id: firstValue(material.id, material.id_ms, material.material_id, material.assortment_id, material.assortmentId, idx),
      assortment_id: firstValue(
        material.assortment_id,
        material.assortmentId,
        material.ms_assortment_id,
        material.id_ms,
        material.assortment?.id
      ),
      name: firstValue(material.name, material.name_ms, material.assortment_name, material.assortment?.name, 'Материал'),
      quantity: asNumber(firstValue(material.quantity, material.qty, material.count), 0),
      type: firstValue(material.type, material.type_ms, material.assortment_type, material.assortment?.type, 'product'),
      has_techcard: Boolean(material.has_techcard ?? material.hasTechcard),
    })),
  };
};

export default function ReturnsReceiving() {
  const isMobile = useIsMobile();
  const [scanner, setScanner] = useState('');
  const [scannedCode, setScannedCode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [returnDoc, setReturnDoc] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [rowResults, setRowResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, item: null, materials: [], techcard: null });
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [treeNodes, setTreeNodes] = useState({});
  const [rootNodeKeys, setRootNodeKeys] = useState([]);
  const [disassembleQty, setDisassembleQty] = useState('1');
  const treeRequestTokens = useRef({});
  const dialogGeneration = useRef(0);

  const items = scanResult?.items || [];
  const order = scanResult?.order || null;
  const returnCreated = Boolean(returnDoc?.id);
  const selectedLeafKeys = useMemo(() => activeSelectedLeafKeys(treeNodes), [treeNodes]);
  const invalidExpandedKeys = useMemo(
    () => expandedBranchesWithoutLeaves(treeNodes),
    [treeNodes],
  );

  const customerOrderHref = useMemo(() => {
    if (!order?.href && !order?.customerorder_id) return '';
    return order.href || `https://online.moysklad.ru/app/#customerorder/edit?id=${order.customerorder_id}`;
  }, [order]);

  const returnHref = useMemo(() => {
    if (!returnDoc?.href && !returnDoc?.id) return '';
    return returnDoc.href || `https://online.moysklad.ru/app/#salesreturn/edit?id=${returnDoc.id}`;
  }, [returnDoc]);

  const resetFlow = () => {
    setScanResult(null);
    setReturnDoc(null);
    setQuantities({});
    setRowResults({});
    setDialog({ open: false, item: null, materials: [], techcard: null });
    setTreeNodes({});
    setRootNodeKeys([]);
    dialogGeneration.current += 1;
  };

  const buildBasePayload = () => ({
    code: scannedCode,
    ms_order_id: order?.customerorder_id,
    order_id: order?.customerorder_id,
    customerorder_id: order?.customerorder_id,
    customer_order_href: order?.href,
    order_name: order?.name,
    order_number: order?.name,
  });

  const setDefaultQuantities = (nextItems) => {
    const defaults = {};
    nextItems.forEach((item, idx) => {
      defaults[item.rowKey || idx] = String(item.quantity || 1);
    });
    setQuantities(defaults);
  };

  const handleScan = async (event) => {
    event.preventDefault();
    const code = scanner.trim();
    if (!code) return;

    setLoading(true);
    setMessage('');
    setError('');
    resetFlow();
    setScannedCode(code);

    try {
      const { data } = await axios.post(`${apiPrefix}/scan`, { code });
      const next = normalizeScanResponse(data);
      setScanResult(next);
      setDefaultQuantities(next.items);
      setMessage('Заказ найден. Создайте возврат покупателя перед дальнейшими действиями.');
      setScanner('');
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message || 'Заказ не найден');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReturn = async () => {
    if (!order) return;
    setActionLoading('create-return');
    setMessage('');
    setError('');

    try {
      const { data } = await axios.post(`${apiPrefix}/create-return`, buildBasePayload());
      const doc = normalizeReturnDoc(data);
      setReturnDoc(doc);
      if (doc.positions.length > 0) {
        setScanResult((prev) => ({ ...prev, items: doc.positions }));
        setDefaultQuantities(doc.positions);
      }
      notifySuccess('Возврат покупателя создан');
      setMessage('Возврат создан на склад возвратов. Теперь можно принять товар или разобрать его.');
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message || 'Не удалось создать возврат');
    } finally {
      setActionLoading('');
    }
  };

  const getQuantity = (item) => Math.max(0, asNumber(quantities[item.rowKey], item.quantity || 1));

  const handleAccept = async (item) => {
    if (!returnCreated) return;
    setActionLoading(`accept-${item.rowKey}`);
    setMessage('');
    setError('');

    try {
      const { data } = await axios.post(`${apiPrefix}/accept`, {
        ...buildBasePayload(),
        salesreturn_id: returnDoc.id,
        salesreturn_href: returnDoc.href,
        assortment_id: item.assortment_id,
        quantity: getQuantity(item),
        item,
        positions: [{ ...item, quantity: getQuantity(item) }],
      });
      setRowResults((prev) => ({
        ...prev,
        [item.rowKey]: {
          severity: 'success',
          text: data?.message || 'Товар принят на склад готовой продукции',
          href: data?.href || data?.move_href,
        },
      }));
      notifySuccess('Перемещение создано');
    } catch (e) {
      setRowResults((prev) => ({
        ...prev,
        [item.rowKey]: {
          severity: 'error',
          text: e.response?.data?.message || e.response?.data?.error || e.message || 'Ошибка приемки',
        },
      }));
    } finally {
      setActionLoading('');
    }
  };

  const openDisassembleDialog = async (item) => {
    if (!returnCreated || !item.assortment_id) return;
    const generation = dialogGeneration.current + 1;
    dialogGeneration.current = generation;
    setDialog({ open: true, item, materials: [], techcard: null });
    setDisassembleQty(String(getQuantity(item) || 1));
    setTreeNodes({});
    setRootNodeKeys([]);
    setMaterialsLoading(true);
    setError('');

    try {
      const { data } = await axios.get(`${apiPrefix}/materials`, {
        params: { assortment_id: item.assortment_id },
      });
      const next = normalizeMaterials(data);
      if (dialogGeneration.current !== generation) return;
      const rootPath = [normalizeSegment(item)];
      const created = createMaterialNodes({
        materials: next.materials,
        parentPath: rootPath,
      });
      setDialog({ open: true, item, materials: next.materials, techcard: next.techcard });
      setTreeNodes(created.nodes);
      setRootNodeKeys(created.keys);
    } catch (e) {
      if (dialogGeneration.current !== generation) return;
      setDialog({ open: true, item, materials: [], techcard: null });
      setError(e.response?.data?.message || e.response?.data?.error || e.message || 'Не удалось загрузить материалы');
    } finally {
      if (dialogGeneration.current === generation) setMaterialsLoading(false);
    }
  };

  const closeDialog = () => {
    dialogGeneration.current += 1;
    setDialog({ open: false, item: null, materials: [], techcard: null });
    setTreeNodes({});
    setRootNodeKeys([]);
    treeRequestTokens.current = {};
  };

  const setNodeSelected = (key, selected) => {
    setTreeNodes((prev) => ({
      ...prev,
      [key]: { ...prev[key], selected, expanded: selected ? false : prev[key].expanded },
    }));
  };

  const cancelNodeDisassembly = (key) => {
    treeRequestTokens.current[key] = (treeRequestTokens.current[key] || 0) + 1;
    setTreeNodes((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        selected: true,
        expanded: false,
        loading: false,
        error: '',
      },
    }));
  };

  const expandNode = async (key) => {
    const node = treeNodes[key];
    if (!node || node.loading || node.expanded) return;
    if (node.childrenKeys?.length) {
      setTreeNodes((prev) => ({
        ...prev,
        [key]: { ...prev[key], selected: false, expanded: true, error: '' },
      }));
      return;
    }
    const requestToken = (treeRequestTokens.current[key] || 0) + 1;
    const generation = dialogGeneration.current;
    treeRequestTokens.current[key] = requestToken;
    setTreeNodes((prev) => ({
      ...prev,
      [key]: { ...prev[key], selected: false, loading: true, error: '' },
    }));
    try {
      const { data } = await axios.get(`${apiPrefix}/materials`, {
        params: {
          assortment_id: node.assortment_id,
          type: node.type,
        },
      });
      if (
        dialogGeneration.current !== generation
        || treeRequestTokens.current[key] !== requestToken
      ) return;
      const next = normalizeMaterials(data);
      if (!next.materials.length) throw new Error('В техкарте нет материалов');
      const created = createMaterialNodes({
        materials: next.materials,
        parentPath: node.path,
        parentKey: key,
        parentMultiplier: node.multiplier,
        depth: node.depth + 1,
      });
      setTreeNodes((prev) => ({
        ...prev,
        ...created.nodes,
        [key]: {
          ...prev[key],
          selected: false,
          expanded: true,
          loading: false,
          error: '',
          childrenKeys: created.keys,
        },
      }));
    } catch (e) {
      if (
        dialogGeneration.current !== generation
        || treeRequestTokens.current[key] !== requestToken
      ) return;
      setTreeNodes((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          selected: true,
          expanded: false,
          loading: false,
          error: e.response?.data?.message || e.message || 'Не удалось загрузить техкарту',
        },
      }));
    }
  };

  const handleDisassemble = async () => {
    const item = dialog.item;
    if (!item || !returnCreated) return;

    setActionLoading(`disassemble-${item.rowKey}`);
    setMessage('');
    setError('');

    if (!selectedLeafKeys.length) {
      setError('Выберите хотя бы один конечный или неразобранный материал.');
      setActionLoading('');
      return;
    }
    if (invalidExpandedKeys.length) {
      setError('В каждой раскрытой ветке выберите хотя бы один материал.');
      setActionLoading('');
      return;
    }

    try {
      const disassemblyTree = buildDisassemblyDeclaration(treeNodes);
      const { data } = await axios.post(`${apiPrefix}/disassemble`, {
        ...buildBasePayload(),
        salesreturn_id: returnDoc.id,
        salesreturn_href: returnDoc.href,
        assortment_id: item.assortment_id,
        quantity: Math.max(0, asNumber(disassembleQty, 1)),
        item,
        disassembly_tree: disassemblyTree,
      });
      setRowResults((prev) => ({
        ...prev,
        [item.rowKey]: {
          severity: toArray(data?.warnings).length ? 'warning' : 'success',
          text: data?.message || 'Разбор оформлен',
          warnings: toArray(data?.warnings),
          priceSources: [...toArray(data?.positions), data?.source_position].filter(Boolean),
          documents: [
            data?.loss_href && { label: 'Открыть списание', href: data.loss_href },
            data?.enter_href && { label: 'Открыть оприходование', href: data.enter_href },
          ].filter(Boolean),
        },
      }));
      notifySuccess('Разбор оформлен');
      closeDialog();
    } catch (e) {
      const response = e.response?.data || {};
      const text = response.message || response.error || e.message || 'Не удалось оформить разбор';
      if (response.loss_id || response.loss_href) {
        setRowResults((prev) => ({
          ...prev,
          [item.rowKey]: {
            severity: 'error',
            text,
            warnings: toArray(response.warnings),
            priceSources: [...toArray(response?.positions), response?.source_position].filter(Boolean),
            manualAction: response.manual_action,
            documents: [
              response.loss_href && { label: 'Открыть созданное списание', href: response.loss_href },
              response.enter_href && { label: 'Открыть оприходование', href: response.enter_href },
            ].filter(Boolean),
          },
        }));
        closeDialog();
      } else {
        setError(text);
      }
    } finally {
      setActionLoading('');
    }
  };

  const renderRowResult = (result) => {
    if (!result) return null;
    return (
      <Alert severity={result.severity} sx={{ py: 0 }}>
        <Stack spacing={0.5}>
          <Typography component="span" variant="body2">{result.text}</Typography>
          {result.manualAction && (
            <Typography component="span" variant="body2" fontWeight="medium">
              {result.manualAction}
            </Typography>
          )}
          {toArray(result.warnings).map((warning, index) => (
            <Typography component="span" variant="body2" key={`${warning}-${index}`}>
              {warning}
            </Typography>
          ))}
          {toArray(result.priceSources).map((position, index) => (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              key={`${position.assortment_id || position.name || index}-price-source`}
            >
              {position.name || 'Позиция'}: {PRICE_SOURCE_LABELS[position.price_source] || position.price_source || 'источник не указан'}
              {position.price_date ? ` (${position.price_date})` : ''}
            </Typography>
          ))}
          {toArray(result.documents).map((document) => (
            <Link key={document.href} href={document.href} target="_blank" rel="noreferrer">
              {document.label}
            </Link>
          ))}
          {result.href && (
            <Link href={result.href} target="_blank" rel="noreferrer">
              Открыть документ
            </Link>
          )}
        </Stack>
      </Alert>
    );
  };

  const renderItemActions = (item) => {
    const rowLoading = actionLoading === `accept-${item.rowKey}` || actionLoading === `disassemble-${item.rowKey}`;
    const disabled = !returnCreated || rowLoading || !item.assortment_id;

    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          variant="contained"
          size="small"
          disabled={disabled}
          onClick={() => handleAccept(item)}
        >
          {actionLoading === `accept-${item.rowKey}` ? 'Приёмка…' : 'Принять'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          onClick={() => openDisassembleDialog(item)}
        >
          {actionLoading === `disassemble-${item.rowKey}` ? 'Разбор…' : 'Разобрать'}
        </Button>
      </Stack>
    );
  };

  const renderQtyInput = (item) => (
    <TextField
      type="number"
      label="Кол-во"
      size="small"
      value={quantities[item.rowKey] || String(item.quantity || 1)}
      onChange={(event) => setQuantities((prev) => ({ ...prev, [item.rowKey]: event.target.value }))}
      inputProps={{ min: 0, step: 1 }}
      sx={{ width: 110 }}
    />
  );

  const renderMaterialNode = (key) => {
    const node = treeNodes[key];
    if (!node) return null;
    const ownIdentity = `${node.type}:${node.assortment_id}`;
    const hasCycle = node.path
      .slice(0, -1)
      .some((segment) => `${segment.type}:${segment.assortment_id}` === ownIdentity);
    const canExpand = node.has_techcard && !hasCycle && node.depth < 7;
    const total = node.multiplier * Math.max(0, asNumber(disassembleQty, 1));
    const indent = Math.min(node.depth, 4) * 16;

    return (
      <Box key={key} sx={{ ml: `${indent}px`, borderLeft: node.depth ? 2 : 0, borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', sm: 'auto minmax(0, 1fr) 110px 90px' },
            gap: 1,
            alignItems: 'center',
            px: 1,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: node.expanded ? 'action.hover' : 'transparent',
          }}
        >
          <Checkbox
            checked={Boolean(node.selected && !node.expanded)}
            disabled={node.expanded || node.loading}
            onChange={(event) => setNodeSelected(key, event.target.checked)}
            inputProps={{ 'aria-label': `Оставить ${node.name}` }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight="medium">{node.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {node.assortment_id}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {node.expanded ? (
                <Button size="small" color="secondary" onClick={() => cancelNodeDisassembly(key)}>
                  Отменить разбор
                </Button>
              ) : canExpand ? (
                <Button size="small" variant="outlined" onClick={() => expandNode(key)} disabled={node.loading}>
                  {node.loading ? 'Загрузка…' : 'Разобрать'}
                </Button>
              ) : null}
              {hasCycle && <Chip size="small" color="warning" label="Цикл техкарт" />}
              {node.depth >= 7 && node.has_techcard && (
                <Chip size="small" color="warning" label="Макс. глубина" />
              )}
            </Stack>
            {node.loading && <CircularProgress size={16} sx={{ mt: 1 }} />}
            {node.error && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{node.error}</Alert>}
          </Box>
          <Typography variant="body2" textAlign="right" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {formatQty(node.multiplier)}
          </Typography>
          <Typography variant="body2" textAlign="right" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {formatQty(total)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' }, gridColumn: '2' }}>
            На 1 исходный: {formatQty(node.multiplier)} · Итого: {formatQty(total)}
          </Typography>
        </Box>
        <Collapse in={node.expanded} timeout="auto" unmountOnExit={false}>
          <Box>
            {(node.childrenKeys || []).map(renderMaterialNode)}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Приёмка возвратов
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Сканируйте ШК МП или номер стикера, создайте связанный возврат в МойСклад, затем примите товар или оформите разбор.
      </Typography>

      <Paper component="form" onSubmit={handleScan} sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            label="ШК МП / номер стикера"
            value={scanner}
            onChange={(event) => setScanner(event.target.value)}
            autoFocus
            size="small"
            sx={{ minWidth: 280, flex: 1 }}
          />
          <Button type="submit" variant="contained" disabled={loading || !scanner.trim()}>
            {loading ? <CircularProgress size={20} /> : 'Найти заказ'}
          </Button>
          <Button variant="outlined" onClick={resetFlow} disabled={loading || (!scanResult && !returnDoc)}>
            Очистить
          </Button>
        </Stack>
        {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      {order && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                Найденный заказ
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
                {customerOrderHref ? (
                  <Link href={customerOrderHref} target="_blank" rel="noreferrer" underline="hover">
                    {order.name}
                  </Link>
                ) : (
                  <Typography>{order.name}</Typography>
                )}
                {order.marketplace && <Chip label={order.marketplace} size="small" variant="outlined" />}
                {order.stickerNum && <Chip label={`Стикер ${order.stickerNum}`} size="small" />}
                {order.barcodeMp && <Chip label={`ШК ${order.barcodeMp}`} size="small" />}
              </Stack>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              {returnDoc?.id && (
                <Chip
                  label={returnHref ? `Возврат ${returnDoc.name}` : 'Возврат создан'}
                  color="success"
                  component={returnHref ? 'a' : 'div'}
                  href={returnHref || undefined}
                  clickable={Boolean(returnHref)}
                  target={returnHref ? '_blank' : undefined}
                />
              )}
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCreateReturn}
                disabled={returnCreated || actionLoading === 'create-return' || !order.customerorder_id}
              >
                {actionLoading === 'create-return' ? 'Создание…' : 'Создать возврат'}
              </Button>
            </Stack>
          </Stack>
          {!returnCreated && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Действия по товарам заблокированы до создания документа возврата покупателя.
            </Alert>
          )}
          {order.blocked_reason && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {order.blocked_reason}
            </Alert>
          )}
        </Paper>
      )}

      {scanResult && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
            Товары к обработке
          </Typography>
          {items.length === 0 ? (
            <Alert severity="warning">В заказе не найдены позиции с ассортиментом МойСклад.</Alert>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {items.map((item) => (
                <Card key={item.rowKey} variant="outlined">
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Stack spacing={1}>
                      <Box>
                        <Typography fontWeight="medium">{item.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.assortment_id ? `Ассортимент: ${item.assortment_id}` : 'Нет assortment id'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={`В возврате: ${formatQty(item.quantity)}`} size="small" />
                        {renderQtyInput(item)}
                      </Stack>
                      {renderItemActions(item)}
                      {renderRowResult(rowResults[item.rowKey])}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Товар</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell align="right">В возврате</TableCell>
                    <TableCell>Кол-во</TableCell>
                    <TableCell>Действия</TableCell>
                    <TableCell>Результат</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.rowKey} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.assortment_id || 'Нет assortment id'}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.type}</TableCell>
                      <TableCell align="right">{formatQty(item.quantity)}</TableCell>
                      <TableCell>{renderQtyInput(item)}</TableCell>
                      <TableCell>{renderItemActions(item)}</TableCell>
                      <TableCell>
                        {renderRowResult(rowResults[item.rowKey])}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Разобрать товар</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography fontWeight="medium">{dialog.item?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Выберите пригодные компоненты для одного оприходования. Исходный товар будет полностью списан;
                невыбранные компоненты в документы не попадут.
              </Typography>
            </Box>
            <TextField
              type="number"
              label="Количество товара для разбора"
              size="small"
              value={disassembleQty}
              onChange={(event) => setDisassembleQty(event.target.value)}
              inputProps={{ min: 0, step: 1 }}
              sx={{ width: 220 }}
            />
            {dialog.techcard?.name && (
              <Alert severity="info">
                Техкарта: {dialog.techcard.name}{dialog.techcard.source ? ` (${dialog.techcard.source})` : ''}
              </Alert>
            )}
            {!materialsLoading && dialog.materials.length > 0 && dialog.techcard?.message && (
              <Alert severity="warning">{dialog.techcard.message}</Alert>
            )}
            {materialsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : dialog.materials.length === 0 ? (
              <Alert severity="warning">
                {dialog.techcard?.message || 'Материалы по техкарте не найдены.'} Разбор недоступен.
              </Alert>
            ) : (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'grid' },
                    gridTemplateColumns: 'auto minmax(0, 1fr) 110px 90px',
                    gap: 1,
                    px: 1,
                    py: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="caption">Оставить</Typography>
                  <Typography variant="caption">Материал</Typography>
                  <Typography variant="caption" textAlign="right">На 1 исходный</Typography>
                  <Typography variant="caption" textAlign="right">Итого</Typography>
                </Box>
                {rootNodeKeys.map(renderMaterialNode)}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleDisassemble}
            disabled={
              materialsLoading
              || dialog.materials.length === 0
              || selectedLeafKeys.length === 0
              || invalidExpandedKeys.length > 0
              || asNumber(disassembleQty, 0) <= 0
              || actionLoading.startsWith('disassemble-')
            }
          >
            {actionLoading.startsWith('disassemble-') ? 'Оформление…' : 'Оформить разбор'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
